import { Facebook, Instagram, Megaphone, Youtube } from "lucide-react";
import { SITE } from "@/config/site";
import { ChapterHeading } from "@/components/ChapterHeading";
import { Reveal } from "@/components/Reveal";
import { trackEvent, trackOutbound, withUtm } from "@/lib/analytics";

const CARD =
  "flex h-full flex-col rounded-3xl border border-[#D4AF37]/20 bg-[#161310] p-8 text-center transition-[border-color,box-shadow] duration-500 hover:border-[#D4AF37]/50";
const BTN =
  "inline-flex min-h-[52px] w-full items-center justify-center gap-3 rounded-full border border-[#D4AF37]/50 bg-[#D4AF37]/[0.06] px-6 text-sm font-semibold uppercase tracking-[0.1em] text-[#F3E5AB] transition-colors duration-300 hover:bg-[#D4AF37]/[0.14] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#E6CA65]";
const BTN_DISABLED =
  "inline-flex min-h-[52px] w-full cursor-not-allowed items-center justify-center gap-3 rounded-full border border-[#D4AF37]/15 px-6 text-sm font-semibold uppercase tracking-[0.1em] text-[#918A7E]";

export function SocialSection() {
  const channels = [
    {
      key: "instagram",
      icon: Instagram,
      title: "Instagram",
      text: "Artist reveals, announcements and behind-the-scenes moments from the mehfil.",
      url: SITE.instagramUrl,
      label: "Follow us on Instagram",
      event: "instagram_click",
      testId: "social-instagram-btn",
    },
    {
      key: "facebook",
      icon: Facebook,
      title: "Facebook",
      text: "Event updates and community conversations for Chitti Ayee Hai.",
      url: SITE.facebookUrl,
      label: "Follow us on Facebook",
      event: "facebook_click",
      testId: "social-facebook-btn",
    },
    {
      key: "whatsapp-channel",
      icon: Megaphone,
      title: "WhatsApp Channel",
      text: "The fastest way to hear every announcement — straight to your WhatsApp.",
      url: SITE.whatsappChannelUrl,
      label: "Join our WhatsApp Channel",
      event: "whatsapp_click",
      testId: "social-whatsapp-channel-btn",
    },
    {
      key: "youtube",
      icon: Youtube,
      title: "YouTube",
      text: "Performances and official event videos on the channel.",
      url: SITE.youtubeUrl,
      label: "Watch on YouTube",
      event: "youtube_click",
      testId: "social-youtube-btn",
    },
  ];

  return (
    <section id="social" data-testid="social-section" className="relative px-5 py-20 sm:px-8 sm:py-28 lg:py-32">
      <div className="mx-auto max-w-6xl">
        <ChapterHeading number="08" title="Stay Connected" testId="social-heading" />
        <div className="mt-14 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {channels.map((c, i) => {
            const Icon = c.icon;
            return (
              <Reveal key={c.key} delay={i * 0.08} className="h-full">
                <div data-testid={`social-card-${c.key}`} className={CARD}>
                  <span className="mx-auto inline-flex h-14 w-14 items-center justify-center rounded-full border border-[#D4AF37]/35 bg-[#D4AF37]/[0.08]">
                    <Icon className="h-6 w-6 text-[#E6CA65]" aria-hidden="true" />
                  </span>
                  <h3 className="font-display mt-5 text-xl text-[#F7F4EF] sm:text-2xl">{c.title}</h3>
                  <p className="mt-3 flex-1 text-sm leading-[1.8] text-[#918A7E] sm:text-base">
                    {c.text}
                  </p>
                  <div className="mt-6">
                    {c.url ? (
                      <a
                        href={withUtm(c.url)}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid={c.testId}
                        onClick={() => {
                          trackEvent(c.event);
                          trackOutbound(c.key);
                        }}
                        className={BTN}
                      >
                        <Icon className="h-4 w-4 shrink-0" aria-hidden="true" />
                        {c.label}
                      </a>
                    ) : (
                      <span data-testid={c.testId} className={BTN_DISABLED}>
                        <Icon className="h-4 w-4 shrink-0" aria-hidden="true" />
                        Coming Soon
                      </span>
                    )}
                  </div>
                </div>
              </Reveal>
            );
          })}
        </div>
        <Reveal delay={0.2}>
          <p className="mt-10 text-center text-sm leading-relaxed text-[#918A7E] sm:text-base">
            For enquiries &amp; collaborations:{" "}
            <a
              href={`https://wa.me/${SITE.whatsappNumber}`}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="social-whatsapp-enquiries-link"
              onClick={() => {
                trackEvent("whatsapp_click");
                trackOutbound("whatsapp-enquiries");
              }}
              className="font-semibold text-[#F3E5AB] underline decoration-[#D4AF37]/40 underline-offset-4 transition-colors hover:text-[#E6CA65]"
            >
              WhatsApp +91 93539 52967
            </a>
            {" · "}
            <a
              href={`mailto:${SITE.contactEmail}`}
              data-testid="social-email-link"
              onClick={() => trackOutbound("email")}
              className="font-semibold text-[#F3E5AB] underline decoration-[#D4AF37]/40 underline-offset-4 transition-colors hover:text-[#E6CA65]"
            >
              {SITE.contactEmail}
            </a>
          </p>
        </Reveal>
      </div>
    </section>
  );
}
